// Idle channels (#596, #601): with "Group idle channels" on, a channel whose newest message is 24 hours old or more, or that has
// none, leaves its category (or the flat list) for one "Idle" group at the bottom of the Classic list. Worked out here from the
// Chats rows' times and never stored. The rule is shared/src/idleChannels.ts (cases: shared/cases/idle-channels.json); this
// is the list around it, as ios/Bloom/Live/IdleChannels.swift `split` has it.
import { isIdleChannel, type Channel } from "@bloom/shared";
import { flatChannels } from "./listOptions.ts";

export interface IdleSplit {
  /** The text channels that stay in the list. */
  rest: Channel[];
  /** The idle channels, as the flat list orders them: A to Z, or the newest message first when `byActivity`. */
  idle: Channel[];
  /** The groups (a category id, or null for the channels with no category) that held channels and the rule emptied: their header leaves unless `keepsHeader`. */
  emptied: Set<string | null>;
}

export interface IdleInput {
  /** Every text channel (no DMs). */
  channels: Channel[];
  /** Ms since 1970, to judge at. */
  now: number;
  /** The open channel: it stays. */
  activeId: string | undefined;
  /** A channel's unread @mentions: a channel with one stays. */
  mentions: (id: string) => number;
  /** Sort by activity, which only counts while the list is flat. */
  byActivity: boolean;
  /** A channel's newest message time (ms); undefined when it has none. */
  newest: (id: string) => number | undefined;
  /** The channels that have a Chats row. A channel with no row yet is not idle: before the rows arrive, "no message" and "not loaded" look alike, and the whole list would flash into Idle. */
  known: (id: string) => boolean;
  /** The person sees the + that makes a channel in a group (an owner or admin): then an emptied header stays, as the way to make one there. */
  keepsHeader: boolean;
}

export function splitIdle(i: IdleInput): IdleSplit {
  const rest: Channel[] = [];
  const idle: Channel[] = [];
  const had = new Set<string | null>();
  const kept = new Set<string | null>();
  for (const c of i.channels) {
    had.add(c.categoryId);
    const goes = i.known(c.id) && isIdleChannel({ newestAt: i.newest(c.id) ?? null, now: i.now, open: c.id === i.activeId, mention: i.mentions(c.id) > 0 });
    if (goes) idle.push(c);
    else {
      rest.push(c);
      kept.add(c.categoryId);
    }
  }
  const emptied = new Set<string | null>();
  if (!i.keepsHeader) for (const id of had) if (!kept.has(id)) emptied.add(id);
  return { rest, idle: flatChannels(idle, i.byActivity, i.newest), emptied };
}
